// GenerateBankStore — estado global da geração de simulado a partir de caderno.
//
// Modelo (2026-05-29): a geração é ASSÍNCRONA no backend. O POST cria um job e
// retorna 202 { jobId } na hora; aqui fazemos POLLING do status até terminar.
// Isso resolve o timeout ~100s da Cloudflare (a geração leva 1-6 min) e garante
// que o banco SEMPRE persiste — mesmo se o aluno fechar o modal. Um pill
// flutuante (GenerateBankDock) acompanha; o sininho também avisa.
// [[project-caderno-bank-context]]
//
// Mantém dependências leves (só ./api + friendly-error) pra evitar import
// circular com App.tsx [[feedback-circular-imports-no-top-level-ref]].

import { signal } from "@preact/signals";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

export interface GenResult {
  bank: { id: string; name: string; count: number };
  requested: number;
  delivered: number;
  warnings: string[];
  batchesRun: number;
}

export type BankGenStatus = "generating" | "done" | "error";

export interface BankGenTask {
  status: BankGenStatus;
  /** Job no backend — usado pra polling e cancelamento. */
  jobId: string;
  /** Caderno de origem — usado pra o modal só refletir a tarefa dele. */
  sessionId: string;
  sessionTitle: string;
  requested: number;
  /** Contagem parcial de questões já geradas (progresso live, enquanto roda). */
  delivered?: number | null;
  /** Texto curto de progresso (ex.: "2/5 documentos"). */
  progress?: string | null;
  result: GenResult | null;
  error: string | null;
}

/** Tarefa única em curso (ou null). Só uma geração por vez. */
export const bankGenTask = signal<BankGenTask | null>(null);
/** O modal está aberto? O pill se esconde enquanto sim, pra não duplicar UI. */
export const bankGenModalOpen = signal<boolean>(false);

// ===== Polling =============================================================
const POLL_INTERVAL_MS = 2500;
const POLL_DEADLINE_MS = 12 * 60 * 1000; // desiste de esperar após 12 min
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollDeadline = 0;

function stopPolling() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

interface StatusResp {
  jobId: string;
  status: "running" | "done" | "error" | "cancelled";
  requested: number;
  delivered: number | null;
  progress: string | null;
  bankId: string | null;
  bankName: string | null;
  error: string | null;
  warnings: string[] | null;
}

async function pollOnce(jobId: string) {
  // Tarefa trocou/sumiu enquanto esperávamos — para.
  if (bankGenTask.value?.jobId !== jobId) return;

  if (typeof window !== "undefined" && Date.now() > pollDeadline) {
    bankGenTask.value = {
      ...bankGenTask.value,
      status: "error",
      error:
        "A geração está demorando mais que o normal. Confira o sininho de notificações em alguns minutos — o simulado pode chegar lá."
    };
    stopPolling();
    return;
  }

  try {
    const s = await call<StatusResp>(
      `/api/student/caderno/generate-bank/status?jobId=${encodeURIComponent(jobId)}`
    );
    if (bankGenTask.value?.jobId !== jobId) return; // mudou no meio

    if (s.status === "done") {
      bankGenTask.value = {
        ...bankGenTask.value,
        status: "done",
        result: {
          bank: {
            id: s.bankId ?? "",
            name: s.bankName ?? "Simulado",
            count: s.delivered ?? 0
          },
          requested: s.requested,
          delivered: s.delivered ?? 0,
          warnings: s.warnings ?? [],
          batchesRun: 0
        }
      };
      stopPolling();
    } else if (s.status === "error") {
      bankGenTask.value = {
        ...bankGenTask.value,
        status: "error",
        error: s.error ?? "Falha ao gerar o simulado."
      };
      stopPolling();
    } else if (s.status === "cancelled") {
      bankGenTask.value = null;
      stopPolling();
    } else {
      // running — reflete o progresso parcial (delivered + progress) e agenda.
      if (bankGenTask.value && (typeof s.delivered === "number" || s.progress)) {
        bankGenTask.value = {
          ...bankGenTask.value,
          delivered: typeof s.delivered === "number" ? s.delivered : bankGenTask.value.delivered,
          progress: s.progress ?? bankGenTask.value.progress
        };
      }
      pollTimer = setTimeout(() => void pollOnce(jobId), POLL_INTERVAL_MS);
    }
  } catch {
    // Erro transitório de rede no polling — tenta de novo (até o deadline).
    pollTimer = setTimeout(() => void pollOnce(jobId), POLL_INTERVAL_MS);
  }
}

export function isBankGenBusy(): boolean {
  return bankGenTask.value?.status === "generating";
}

/**
 * Dispara a geração: POST → 202 { jobId } → inicia polling. Resolve assim que
 * o job é criado (não espera a geração toda). O estado vive no signal.
 */
export async function startBankGeneration(args: {
  sessionId: string;
  sessionTitle: string;
  numQuestions: number;
  payload: Record<string, unknown>;
}): Promise<void> {
  stopPolling();
  // Otimista: já mostra "gerando" enquanto o POST vai e volta.
  bankGenTask.value = {
    status: "generating",
    jobId: "",
    sessionId: args.sessionId,
    sessionTitle: args.sessionTitle,
    requested: args.numQuestions,
    result: null,
    error: null
  };
  try {
    const r = await call<{ jobId: string; status: string; requested: number }>(
      "/api/student/caderno/generate-bank",
      { method: "POST", body: JSON.stringify(args.payload) }
    );
    bankGenTask.value = {
      status: "generating",
      jobId: r.jobId,
      sessionId: args.sessionId,
      sessionTitle: args.sessionTitle,
      requested: r.requested ?? args.numQuestions,
      result: null,
      error: null
    };
    pollDeadline =
      (typeof window !== "undefined" ? Date.now() : 0) + POLL_DEADLINE_MS;
    pollTimer = setTimeout(() => void pollOnce(r.jobId), POLL_INTERVAL_MS);
  } catch (e) {
    // Erros de validação (quota/créditos/etc) voltam aqui na hora.
    bankGenTask.value = {
      status: "error",
      jobId: "",
      sessionId: args.sessionId,
      sessionTitle: args.sessionTitle,
      requested: args.numQuestions,
      result: null,
      error: friendlyError(e)
    };
  }
}

/** Interrompe a geração: pede cancelamento ao backend e limpa a tarefa.
 *  O job marca-se 'cancelled' e a geração em background não cria o banco. */
export function cancelBankGeneration() {
  const jobId = bankGenTask.value?.jobId;
  stopPolling();
  bankGenTask.value = null;
  if (jobId) {
    void call("/api/student/caderno/generate-bank/cancel", {
      method: "POST",
      body: JSON.stringify({ jobId })
    }).catch(() => {
      /* best-effort — se falhar, o job termina e notifica pelo sininho */
    });
  }
}

/** Dispensa o resultado/erro exibido (não cancela geração em curso). */
export function dismissBankGen() {
  if (bankGenTask.value?.status === "generating") return;
  stopPolling();
  bankGenTask.value = null;
}
